/* eslint-disable no-nested-ternary */
import moment from 'moment';
import { useMediaQuery } from 'usehooks-ts';
import { Button } from '@components/Button';
import {
  MdAdd,
  MdCheck,
  MdDelete,
  MdDownload,
  MdUploadFile,
} from 'react-icons/md';
import { Dropzone } from '@components/Dropzone';
import { useState } from 'react';
import { Icon } from '@components/Icon';
import { useAuthHttp } from '../../hooks';
import { ADJUST_STOCK_BY_FILE, GET_SKU_STOCK } from '../../constants';

export function StockAdjustmentForm({
  warehouseMatchId,
  warehouseName,
  hasAdjustedStock,
  onStockAdjusted,
}: {
  warehouseMatchId: string;
  warehouseName: string;
  hasAdjustedStock: boolean;
  onStockAdjusted?: () => void;
}) {
  const authHttp = useAuthHttp();
  const isSmall = useMediaQuery('(min-width: 36em)');
  const isDesktop = useMediaQuery('(min-width: 62em)');
  const authHttpFormData = useAuthHttp('multipart/form-data');

  const [uploadFile, setUploadFile] = useState<any>(undefined);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [hasUploadError, setHasUploadError] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isStockAdjusted, setIsStockAdjusted] = useState(hasAdjustedStock);

  const handleDownloadStockData = async () => {
    try {
      const { data } = await authHttp.get(GET_SKU_STOCK);
      const blob = new Blob([data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      const currentDate = moment();
      link.download = `StockData-${currentDate.format('yyyy-MM-DD')}.csv`;
      link.click();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const handleUploadSkuStockData = async () => {
    try {
      setIsUploadLoading(true);
      const { data } = await authHttpFormData.post(
        ADJUST_STOCK_BY_FILE,
        {
          warehouseMatchId,
          stockLevelFile: uploadFile,
        },
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (data) {
        setUploadFile(undefined);
        setHasUploadError(false);
        setIsStockAdjusted(true);
        onStockAdjusted?.();
      }
    } catch (error: any) {
      const errorResponse = error as any;
      if (
        errorResponse.response.data.errors?.length > 0 &&
        !!errorResponse.response.data.errors[0].errorMessage &&
        errorResponse.response.data.errors[0].errorMessage.includes(
          `Stock adjustment for warehouse match ${warehouseMatchId} has already been made.`
        )
      ) {
        setUploadError(`Stock adjustment has already been made.`);
        setHasUploadError(true);
      }
    } finally {
      setIsUploadLoading(false);
    }
  };

  return isStockAdjusted ? (
    <div className='flex flex-col items-center justify-center px-0 py-12'>
      <MdCheck size='8rem' aria-hidden='true' color='green' />
      <div className='text-2xl font-semibold mt-4'>
        Stock adjusted successfully
      </div>
    </div>
  ) : (
    <div className='flex flex-col items-center justify-center px-0 py-12 w-full'>
      <div className='flex flex-col gap-6 w-full max-w-xl items-center'>
        <div className='flex flex-col gap-3 text-gray-700'>
          <p>
            Upload a file below to perform a one-time stock adjustment for{' '}
            <span className='font-semibold'>{warehouseName}</span>.
          </p>
          <p>
            <span className='text-yellow-700 font-semibold'>Warning:</span> This
            can only be done <b>once</b> per warehouse.
          </p>
          <p>
            You can access the sku template by clicking the download button
            below.
          </p>
        </div>
        <div>
          <Button
            variant='primary'
            action='cta'
            size={isDesktop ? 'md' : 'sm'}
            leftIcon={<MdDownload size='1.5rem' />}
            isFullWidth={false}
            onClick={handleDownloadStockData}
            className='max-w-[50%]'
          >
            Download excel template
          </Button>
        </div>
        <div className='w-full max-w-xs'>
          {uploadFile ? (
            <div className='flex flex-col gap-3 w-full'>
              <div className='flex flex-row gap-2 items-center'>
                <MdUploadFile size='1rem' />
                <span className='font-semibold text-base'>
                  {uploadFile.name}
                </span>
              </div>
              <Button
                variant='primary'
                action='cta'
                size={isDesktop ? 'md' : 'sm'}
                leftIcon={<MdUploadFile size='1.5rem' />}
                onClick={handleUploadSkuStockData}
                isLoading={isUploadLoading}
                loadingIcon={<Icon name='loading' />}
                loadingText='Uploading...'
              >
                Upload Stock Data
              </Button>
              <Button
                variant='primary'
                action='danger'
                leftIcon={<MdDelete size='1rem' />}
                size={isDesktop ? 'md' : 'sm'}
                onClick={() => {
                  setHasUploadError(false);
                  setUploadFile(undefined);
                }}
              >
                Clear selected file
              </Button>
              {hasUploadError && (
                <div className='flex flex-col gap-1 mt-2'>
                  <span className='text-red-600 font-semibold'>
                    Error uploading file.{' '}
                    {uploadError
                      ? 'Please fix the following errors and try again.'
                      : 'Please try again.'}
                  </span>
                  <span className='text-red-600'>{uploadError}</span>
                </div>
              )}
            </div>
          ) : (
            <Dropzone
              id='product-master-dropzone'
              acceptAnyFile
              primaryAction={
                <Button
                  size={isDesktop ? 'xs' : isSmall ? 'sm' : 'xs'}
                  leftIcon={<MdAdd size={isSmall ? '1.25rem' : '1rem'} />}
                >
                  Upload Stock Data - {warehouseName}
                </Button>
              }
              onDrop={(acceptedFiles: any) => {
                setUploadFile(acceptedFiles[0]);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
